import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  green: '#339166',
  white: '#FFFFFF',
  lightGray: '#f8fafc',
  darkText: '#1a365d'
};

const DEFAULT_DATA = {
  heading: "Chairman's Message",
  subheading: "Our Commitment to Excellence in Healthcare",
  message: `Warm Wellness Center is a state-of-the-art, modern diagnostic center and specialized doctor's chamber dedicated to providing exceptional medical care. 

Our mission is to create a healthcare environment where patients receive accurate diagnoses, compassionate treatment, and personalized care. We combine advanced medical technology with the expertise of our specialists to deliver healthcare services you can trust.

As we continue to grow, our commitment remains steadfast - to serve our community with integrity, innovation, and the highest standards of medical excellence.`,
  chairman: {
    name: "Md. Sohel Rana",
    position: "Founder and Chairman",
    image: "https://jgfacility.com/backend/media/uploaded_images/message.jpg",
    signature: ""
  }
};

const Message = ({ isAdmin = false, onEdit }) => {
  const [messageData, setMessageData] = useState(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMessageData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/about/message/`);
        if (response.ok) {
          const data = await response.json();
          setMessageData(data);
        } else {
          console.error('Failed to fetch message data');
        }
      } catch (error) {
        console.error('Error fetching message data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessageData();
  }, []);

  if (isLoading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Chairman's Photo */}
          <motion.div 
            className="lg:w-1/3 relative"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={messageData.chairman?.image || DEFAULT_DATA.chairman.image} 
                alt={messageData.chairman?.name || "Chairman"}
                className="w-full h-full lg:object-cover object-center"
              />
              <div 
                className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full border-4 border-white"
                style={{ backgroundColor: COLORS.tertiary }}
              ></div>
            </div>
          </motion.div>

          {/* Message Content */}
          <div className="lg:w-2/3">
            <div className="mb-8">
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4"
                style={{ 
                  backgroundColor: `${COLORS.primary}10`,
                  color: COLORS.primary
                }}
              >
                Leadership
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.darkText }}>
                {messageData.heading || DEFAULT_DATA.heading}
              </h2>
              <div className="w-16 h-1 mb-6 rounded-full" style={{ 
                background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` 
              }}></div>
              <h3 className="text-xl mb-6" style={{ color: COLORS.tertiary }}>
                {messageData.subheading || DEFAULT_DATA.subheading}
              </h3>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 md:p-8 mb-8 relative">
              <div 
                className="absolute top-0 left-0 w-2 h-full rounded-l-xl"
                style={{ backgroundColor: COLORS.primary }}
              ></div>
              <p className="text-gray-700 whitespace-pre-line pl-4">
                {messageData.message || DEFAULT_DATA.message}
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold" style={{ color: COLORS.primary }}>
                {messageData.chairman?.name || DEFAULT_DATA.chairman.name}
              </h4>
              <p className="text-gray-600">{messageData.chairman?.position || DEFAULT_DATA.chairman.position}</p>
              {messageData.chairman?.signature && (
                <div className="mt-4">
                  <img 
                    src={messageData.chairman.signature} 
                    alt="Signature" 
                    className="h-16 object-contain"
                  />
                </div>
              )}
             
            </div>

            {isAdmin && (
              <div className="mt-8">
                <button 
                  onClick={onEdit}
                  className="btn btn-primary"
                >
                  Edit Message
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Message;